import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { checkAndSyncUserPlan, SubscriptionData } from '../services/firestoreService';
import toast from 'react-hot-toast';

export interface PlanSyncState {
  subscription: SubscriptionData | null;
  loading: boolean;
  error: string | null;
  lastSyncAt: number | null;
}

export interface PlanSyncActions {
  syncNow: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

/**
 * Hook to manage plan synchronization between Clerk and Firestore
 * This hook automatically syncs plans and provides manual sync capabilities
 */
export const usePlanSync = (
  autoSync: boolean = true,
  syncInterval: number = 60 * 1000 // 1 minute by default (more aggressive)
): PlanSyncState & PlanSyncActions => {
  const auth = useAuth(); // Get the full auth object
  const { userId } = auth;
  const [state, setState] = useState<PlanSyncState>({
    subscription: null,
    loading: true,
    error: null,
    lastSyncAt: null,
  });

  // Add a ref to track if sync is in progress to prevent infinite loops
  const syncInProgress = useRef(false);
  const hasInitialSync = useRef(false);
  
  // Store auth in a ref to avoid dependency issues
  const authRef = useRef(auth);
  authRef.current = auth;

  const syncPlan = useCallback(async (showToast: boolean = false) => {
    const currentAuth = authRef.current;
    const currentUserId = currentAuth?.userId;
    
    if (!currentUserId || !currentAuth || syncInProgress.current) {
      if (!currentUserId || !currentAuth) {
        setState(prev => ({ ...prev, loading: false, error: 'User not authenticated' }));
      }
      return;
    }

    // Prevent concurrent syncs
    syncInProgress.current = true;

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const subscription = await checkAndSyncUserPlan(
        currentUserId,
        currentAuth, // Pass the auth object instead of user
        (oldPlan, newPlan) => {
          
          const planDisplayNames: Record<string, string> = {
            'free_user': 'Free Plan',
            '2k_credits': '2K Credits Plan',
            '10k_credits': '10K Credits Plan',
            '20k_credits': '20K Credits Plan',
          };

          const newDisplayName = planDisplayNames[newPlan] || newPlan;

          // Always show toast for plan changes, regardless of showToast parameter
          if (newPlan === 'free_user') {
            toast.success(`Subscription ended. Restored to ${newDisplayName}.`);
          } else {
            toast.success(`Plan updated! Welcome to ${newDisplayName}!`);
          }
          
        }
      );

      setState(prev => ({
        ...prev,
        subscription,
        loading: false,
        error: null,
        lastSyncAt: Date.now(),
      }));
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to sync plan';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      console.error('❌ Plan sync error:', error);
      
      // Show error toast if it's a manual sync
      if (showToast) {
        toast.error('Failed to sync subscription plan. Please try again.');
      }
    } finally {
      // Reset sync flag
      syncInProgress.current = false;
    }
  }, []); // Remove all dependencies to stabilize the function

  const syncNow = useCallback(async () => {
    await syncPlan(true); // Show toast for manual sync
  }, [syncPlan]);

  const refreshSubscription = useCallback(async () => {
    await syncPlan(false); // Don't show toast for refresh
  }, [syncPlan]);

  // Initial sync on mount and when auth/userId changes (but only once per session)
  useEffect(() => {
    if (userId && auth && !hasInitialSync.current) {
      hasInitialSync.current = true;
      syncPlan(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, syncPlan]); // Remove auth from dependencies

  // Auto-sync at intervals if enabled - use stable dependencies
  useEffect(() => {
    if (!autoSync || !userId) {
      return;
    }

    const interval = setInterval(() => {
      syncPlan(false);
    }, syncInterval);

    return () => {
      clearInterval(interval);
    };
  }, [autoSync, syncInterval, userId, syncPlan]); // Remove auth from dependencies

  // Sync when the window becomes visible (user comes back to the site)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userId && !syncInProgress.current) {
        syncPlan(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userId, syncPlan]); // Remove auth from dependencies

  return {
    ...state,
    syncNow,
    refreshSubscription,
  };
}; 